import React from 'react'
import { User, Calendar, Shield, CreditCard, Mail, Trash2, Key, Star } from 'lucide-react'

interface UserMetadata {
  created_via?: string
  stripe_customer_id?: string
  lifetime_access: boolean
}

interface Subscription {
  id: string
  status: string
  current_period_end: number
  cancel_at_period_end: boolean
  plan: string
}

interface UserData {
  id: string
  email: string
  created_at: string
  last_sign_in_at: string | null
  email_confirmed_at: string | null
  user_type: 'free' | 'invited' | 'paid'
  metadata: UserMetadata
  subscription: Subscription | null
}

interface UserListProps {
  users: UserData[]
  onDeleteUser?: (userId: string) => void
  onResetPassword?: (userEmail: string) => void
  onGrantLifetimeAccess?: (userId: string) => void
}

export const UserList: React.FC<UserListProps> = ({
  users,
  onDeleteUser,
  onResetPassword,
  onGrantLifetimeAccess
}) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getUserTypeBadge = (userType: string) => {
    switch (userType) {
      case 'paid':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/50 text-green-300 border border-green-500/30">
            <CreditCard className="w-3 h-3 mr-1" />
            Paid
          </span>
        )
      case 'invited':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-900/50 text-purple-300 border border-purple-500/30">
            <Mail className="w-3 h-3 mr-1" />
            Invited
          </span>
        )
      case 'free':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-900/50 text-gray-300 border border-gray-500/30">
            <User className="w-3 h-3 mr-1" />
            Free
          </span>
        )
      default:
        return null
    }
  }

  const getSubscriptionStatus = (subscription: Subscription | null) => {
    if (!subscription) return null

    const statusColors: Record<string, string> = {
      active: 'bg-green-900/50 text-green-300 border-green-500/30',
      canceled: 'bg-red-900/50 text-red-300 border-red-500/30',
      past_due: 'bg-yellow-900/50 text-yellow-300 border-yellow-500/30',
      unpaid: 'bg-orange-900/50 text-orange-300 border-orange-500/30',
      trialing: 'bg-blue-900/50 text-blue-300 border-blue-500/30',
    }

    const colorClass = statusColors[subscription.status] || 'bg-gray-900/50 text-gray-300 border-gray-500/30'

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colorClass}`}>
        {subscription.status}
      </span>
    )
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12">
        <User className="w-16 h-16 text-light-tertiary mx-auto mb-4" />
        <p className="text-light-tertiary text-lg">No users found</p>
        <p className="text-light-quaternary text-sm mt-2">
          Invite your first user to get started
        </p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-dark-tertiary/30">
            <th className="px-4 py-3 text-left text-xs font-medium text-light-tertiary uppercase tracking-wider">
              User
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-light-tertiary uppercase tracking-wider">
              Type
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-light-tertiary uppercase tracking-wider">
              Subscription
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-light-tertiary uppercase tracking-wider">
              Created
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-light-tertiary uppercase tracking-wider">
              Last Sign In
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-light-tertiary uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-dark-tertiary/30">
          {users.map((user) => (
            <tr
              key={user.id}
              className="hover:bg-dark-tertiary/20 transition-colors"
            >
              <td className="px-4 py-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-light-primary">
                      {user.email}
                    </div>
                    {user.metadata.lifetime_access && (
                      <div className="flex items-center text-xs text-purple-400 mt-1">
                        <Shield className="w-3 h-3 mr-1" />
                        Lifetime Access
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                {getUserTypeBadge(user.user_type)}
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                {user.subscription ? (
                  <div className="space-y-1">
                    {getSubscriptionStatus(user.subscription)}
                    <div className="text-xs text-light-tertiary">
                      {user.subscription.plan}
                    </div>
                  </div>
                ) : (
                  <span className="text-light-tertiary text-sm">—</span>
                )}
              </td>
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="flex items-center text-sm text-light-secondary">
                  <Calendar className="w-4 h-4 mr-2 text-light-tertiary" />
                  {formatDate(user.created_at)}
                </div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-light-secondary">
                {formatDate(user.last_sign_in_at)}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-2">
                  {onGrantLifetimeAccess && user.user_type === 'free' && (
                    <button
                      onClick={() => onGrantLifetimeAccess(user.id)}
                      className="text-yellow-400 hover:text-yellow-300 transition-colors p-2 rounded-lg hover:bg-yellow-600/20"
                      title="Grant Lifetime Access"
                    >
                      <Star className="w-4 h-4" />
                    </button>
                  )}
                  {onResetPassword && (
                    <button
                      onClick={() => onResetPassword(user.email)}
                      className="text-primary-400 hover:text-primary-300 transition-colors p-2 rounded-lg hover:bg-primary-600/20"
                      title="Reset Password"
                    >
                      <Key className="w-4 h-4" />
                    </button>
                  )}
                  {onDeleteUser && (
                    <button
                      onClick={() => onDeleteUser(user.id)}
                      className="text-red-400 hover:text-red-300 transition-colors p-2 rounded-lg hover:bg-red-600/20"
                      title="Delete User"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
